import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { HDate, gematriya } from '@hebcal/core';
import { getDayFacts } from '../../utils/jewishCalendar';
import { isGoldDay } from '../../utils/minyanDay';
import { getCalendarTags } from '../../utils/calendarTags';

const HEBREW_MONTH_NAMES: Record<string, string> = {
  Nisan: 'ניסן',
  Iyyar: 'אייר',
  Sivan: 'סיון',
  Tamuz: 'תמוז',
  Av: 'אב',
  Elul: 'אלול',
  Tishrei: 'תשרי',
  Cheshvan: 'חשון',
  Kislev: 'כסלו',
  Tevet: 'טבת',
  "Sh'vat": 'שבט',
  Adar: 'אדר',
  'Adar I': 'אדר א׳',
  'Adar II': 'אדר ב׳',
};

type Dot = 'fast' | 'roshChodesh' | 'festive' | 'other';

const DOT_CLASS: Record<Dot, string> = {
  fast: 'bg-danger-500',
  roshChodesh: 'bg-weekday-600',
  festive: 'bg-gold-600',
  other: 'bg-ash/70',
};

/** Gold cell for Shabbos / Yom Tov / Yom Kippur; small dots for other occasions (spec section 10). */
function dayMarks(dateStr: string): { gold: boolean; dots: Dot[] } {
  const f = getDayFacts(dateStr);
  const dots: Dot[] = [];
  if (f.minorFast || f.tishaBav) dots.push('fast');
  if (f.roshChodesh && !f.yomTov) dots.push('roshChodesh');
  if (f.chanukahDay || f.purim || f.cholHaMoed) dots.push('festive');
  if (f.erevYomTov || f.erevYomKippur || f.erevTishaBav || getCalendarTags(dateStr, 'en').length > 0) dots.push('other');
  return { gold: isGoldDay(dateStr), dots: dots.slice(0, 3) };
}

function formatGregLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}


function hebrewMonthLabel(hd: HDate, isHebrew: boolean): string {
  const name = hd.getMonthName();
  if (isHebrew) {
    return `${HEBREW_MONTH_NAMES[name] ?? name} ${gematriya(hd.getFullYear())}`;
  }
  return `${name} ${hd.getFullYear()}`;
}

function selectedHebrewLabel(hd: HDate, isHebrew: boolean): string {
  const name = hd.getMonthName();
  if (isHebrew) {
    return `${gematriya(hd.getDate())} ${HEBREW_MONTH_NAMES[name] ?? name} ${gematriya(hd.getFullYear())}`;
  }
  return `${hd.getDate()} ${name} ${hd.getFullYear()}`;
}

type GridCell =
  | { kind: 'empty' }
  | {
      kind: 'day';
      dateStr: string;
      hebrewDay: number;
      gregDay: number;
      gold: boolean;
      dots: Dot[];
    };

interface HebrewDatePickerProps {
  selectedDate: string;
  onChange: (date: string) => void;
}

export default function HebrewDatePicker({ selectedDate, onChange }: HebrewDatePickerProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';

  const [open, setOpen] = useState(false);
  const [viewHDate, setViewHDate] = useState(
    () => new HDate(new Date(`${selectedDate}T12:00:00`))
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedHDate = useMemo(
    () => new HDate(new Date(`${selectedDate}T12:00:00`)),
    [selectedDate]
  );

  const gregSecondary = useMemo(
    () =>
      new Intl.DateTimeFormat(isHebrew ? 'he' : 'en', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(new Date(`${selectedDate}T12:00:00`)),
    [selectedDate, isHebrew]
  );

  const viewMonth = viewHDate.getMonth();
  const viewYear = viewHDate.getFullYear();

  const gridCells = useMemo((): GridCell[] => {
    const daysInMonth = HDate.daysInMonth(viewMonth, viewYear);
    const first = new HDate(1, viewMonth, viewYear);
    const leading = first.getDay();
    const cells: GridCell[] = [];

    for (let i = 0; i < leading; i++) {
      cells.push({ kind: 'empty' });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const hd = new HDate(d, viewMonth, viewYear);
      const dateStr = formatGregLocal(hd.greg());
      cells.push({
        kind: 'day',
        dateStr,
        hebrewDay: hd.getDate(),
        gregDay: hd.greg().getDate(),
        ...dayMarks(dateStr),
      });
    }

    while (cells.length % 7 !== 0) {
      cells.push({ kind: 'empty' });
    }

    return cells;
  }, [viewMonth, viewYear]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const weekdayLabels = isHebrew
    ? ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳']
    : ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const toggleOpen = () => {
    setOpen((wasOpen) => {
      if (!wasOpen) {
        setViewHDate(new HDate(new Date(`${selectedDate}T12:00:00`)));
      }
      return !wasOpen;
    });
  };

  const pickDate = (dateStr: string) => {
    onChange(dateStr);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t('calendar.pickDate')}
        className="w-full rounded-[10px] border border-ink/15 bg-paper text-ink text-start px-3 py-2 hover:bg-fog transition-colors"
      >
        <span className="block text-sm font-semibold leading-snug">
          {selectedHebrewLabel(selectedHDate, isHebrew)}
        </span>
        <span className="block text-xs text-ash mt-0.5">{gregSecondary}</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('calendar.pickDate')}
          className="absolute z-30 top-full mt-1 start-0 end-0 sm:end-auto w-full sm:w-[min(100vw-2rem,20rem)] rounded-[16px] border border-ink/10 bg-paper shadow-lg p-3"
        >
          <div dir="ltr" className="flex items-center justify-between gap-2 mb-3">
            <button
              type="button"
              onClick={() => setViewHDate((v) => v.add(-1, 'M'))}
              className="shrink-0 p-1.5 rounded-[10px] border border-ink/15 text-ink hover:bg-fog transition-colors"
              aria-label={t('calendar.previousMonth', { defaultValue: 'Previous month' })}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-semibold text-ink text-center flex-1 min-w-0">
              {hebrewMonthLabel(viewHDate, isHebrew)}
            </span>
            <button
              type="button"
              onClick={() => setViewHDate((v) => v.add(1, 'M'))}
              className="shrink-0 p-1.5 rounded-[10px] border border-ink/15 text-ink hover:bg-fog transition-colors"
              aria-label={t('calendar.nextMonth', { defaultValue: 'Next month' })}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {weekdayLabels.map((label) => (
              <div
                key={label}
                className="text-center text-[10px] font-medium text-ash py-1"
              >
                {label}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {gridCells.map((cell, idx) => {
              if (cell.kind === 'empty') {
                return <div key={`e-${idx}`} className="aspect-square" aria-hidden />;
              }

              const isSelected = cell.dateStr === selectedDate;
              let cellClass =
                'relative aspect-square flex flex-col items-center justify-center rounded-[8px] text-[11px] leading-none transition-colors ';
              cellClass += cell.gold ? 'bg-gold-50 text-gold-700 font-medium ' : 'text-ink hover:bg-fog ';
              if (isSelected) {
                cellClass += 'ring-2 ring-ink ring-offset-1 ring-offset-paper ';
              }

              return (
                <button
                  key={cell.dateStr}
                  type="button"
                  onClick={() => pickDate(cell.dateStr)}
                  className={cellClass}
                >
                  <span className={isHebrew ? 'font-semibold' : ''}>
                    {isHebrew ? gematriya(cell.hebrewDay) : cell.gregDay}
                  </span>
                  {isHebrew ? (
                    <span className="text-[9px] text-ash mt-0.5 tabular-nums">{cell.gregDay}</span>
                  ) : (
                    <span className="text-[9px] text-ash mt-0.5">{gematriya(cell.hebrewDay)}</span>
                  )}
                  {cell.dots.length > 0 && (
                    <span className="absolute bottom-0.5 inset-x-0 flex justify-center gap-0.5" aria-hidden>
                      {cell.dots.map((d) => (
                        <span key={d} className={`w-1 h-1 rounded-full ${DOT_CLASS[d]}`} />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-ash">
            {(['fast', 'roshChodesh', 'festive', 'other'] as Dot[]).map((d) => (
              <span key={d} className="inline-flex items-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full ${DOT_CLASS[d]}`} aria-hidden />
                {t(`calendar.legend.${d}`)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
